/**
 * Opening a `t.me` link from the admin.
 *
 * Inside a Mini App there is exactly one supported way to do this —
 * `Telegram.WebApp.openTelegramLink` — and Telegram's own SDK says so by
 * intercepting clicks itself: `linkHandler` rewrites any `<a>` pointing at a
 * t.me hostname, but only when `target != '_blank'`.
 *
 * We were calling `window.open(url, '_blank')` from a mutation callback, which
 * is neither an anchor nor a non-blank target, so it missed both routes. The
 * merchant tapped "create my shop bot" inside Telegram and nothing happened —
 * and because `connect_bot` is an optional step, the setup checklist still
 * reported the shop as ready.
 *
 * `window.open` is also the wrong tool on the web here: it runs after an
 * awaited request rather than in the click that started it, which is exactly
 * what popup blockers exist to stop. The caller therefore keeps the URL and
 * renders it as a real link too; this function is the convenience, not the
 * guarantee.
 */

interface TelegramWebAppLinks {
  openTelegramLink?: (url: string) => void;
}

function webAppLinks(): TelegramWebAppLinks | null {
  const app = (window as unknown as { Telegram?: { WebApp?: TelegramWebAppLinks } }).Telegram
    ?.WebApp;
  return app && typeof app.openTelegramLink === 'function' ? app : null;
}

/** True when the link was handed to something that can actually open it. */
export function openTelegramUrl(url: string): boolean {
  const app = webAppLinks();
  if (app?.openTelegramLink) {
    app.openTelegramLink(url);
    return true;
  }
  return window.open(url, '_blank', 'noopener') !== null;
}
